import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import DateHelper from 'src/app/helpers/date-helper';
import { LoadingService } from 'src/app/services/loading.service';
import { SnackService } from 'src/app/services/snack.service';
import { _post } from '../../../../../services/base.service';
import { Observable } from 'rxjs';
import { EmployeeDateTourControllerRequestService } from '../employee-tour-list/employee-tour/service/employee-date-tour-controller-request.service';
import { Tour } from 'src/app/model/tour/tour';
import { TourItem } from 'src/app/model/tour/tour-item';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ConfirmModalComponent } from 'src/app/components/confirm-modal/confirm-modal.component';

@Injectable({
  providedIn: 'root',
})
export class AssignSubProjectToTourControllerRequestService {
  private readonly post: <T>(
    path: string,
    body: AssignSubProjectRequest,
    deserialize: (response: any) => T
  ) => Observable<T>;
  private readonly deserialize = (res: AssignSubProjectResult) => {
    if (res != null && CollectionHelper.isPopulated(res.affectedTours)) {
      res.affectedTours.forEach((t) => this.processTour(t));
    }
    return res;
  };

  constructor(
    http: HttpClient,
    snackService: SnackService,
    loadingService: LoadingService,
    router: Router,
    private employeeDateTourService: EmployeeDateTourControllerRequestService,
    private dialog: MatDialog
  ) {
    const requestServices = {
      http,
      loadingService,
      router,
      snackService,
    };
    this.post = _post('/tours/assignSubProject', requestServices);
  }

  public async assignSubProjectToTour(
    subProjectId: number,
    placerId?: number,
    tourDate?: moment.Moment,
    locked?: boolean,
    subProjectType?: string
  ): Promise<AssignSubProjectResult> {
    // Check placement count before assignment, only for BaseProject (placements)
    if (placerId && tourDate) {
      const shouldRunCapacityCheck = subProjectType === 'BaseProject';

      if (shouldRunCapacityCheck) {
        const shouldProceed = await this.checkPlacementCountAndConfirm(
          placerId,
          tourDate
        );
        if (!shouldProceed) {
          throw new Error('Assignment cancelled by user');
        }
      }
    }

    const request: AssignSubProjectRequest = {
      subProjectId: subProjectId,
      placerId: placerId,
      date: tourDate == null ? null : tourDate.format('YYYYMMDD'),
      locked: locked,
    };
    return this.post('', request, this.deserialize).toPromise();
  }

  private async checkPlacementCountAndConfirm(
    placerId: number,
    tourDate: moment.Moment
  ): Promise<boolean> {
    try {
      const tour: Tour = await this.employeeDateTourService.employeeDateTour(
        placerId,
        tourDate.format('YYYYMMDD')
      );

      if (tour && tour.tourItems) {
        const placements: TourItem[] = tour.tourItems.filter(
          (item: TourItem) =>
            item.subProject != null && item.subProject.type === 'BaseProject'
        );

        const placementCount = placements
          .map((p) => {
            if (p.subProject.hasSecondBoard) return 2;
            return 1;
          })
          .reduce((x, y) => x + y, 0);

        if (placementCount >= 9) {
          return new Promise<boolean>((resolve) => {
            const dialogRef = this.dialog.open(ConfirmModalComponent, {
              data: {
                title: 'Waarschuwing',
                text: 'Let op, je voegt meer dan 9 plaatsingen toe aan de toer.',
                yesText: 'Toch toevoegen',
                noText: 'Annuleren',
              },
              panelClass: 'border-modal-panel-class',
            });
            dialogRef
              .afterClosed()
              .subscribe((result: boolean) => resolve(result));
          });
        }
      }

      return true; // Proceed if count is under 9 or no tour exists yet
    } catch (error) {
      console.warn(
        'Could not check placement count, proceeding with assignment:',
        error
      );
      return true; // If we can't check, allow the assignment to proceed
    }
  }

  public unassignSubProjectFromTour(
    subProjectId: number
  ): Promise<AssignSubProjectResult> {
    return this.assignSubProjectToTour(subProjectId);
  }

  private processTour(tour: AffectedTour): void {
    tour.date = tour.date == null ? null : DateHelper.parseDate(tour.date);
  }
}

interface AssignSubProjectRequest {
  subProjectId: number;
  placerId?: number;
  date?: string | null;
  locked?: boolean;
}

export interface AssignSubProjectResult {
  affectedTours: AffectedTour[];
  tourItemId: number;
}

export interface AffectedTour {
  placerId: number;
  date: Date;
}
